<template>
  <ApiKeyCreatedModal
    v-if="rawKey"
    :raw-key="rawKey"
    @closed="emit('closed')"
  />
  <BittsModal
    v-else
    class="api-key-create-modal"
    title="Create API key"
    variant="data"
    :width="600"
    primary-button-text="Create key"
    secondary-button-text="Cancel"
    :disabled="!canSubmit"
    :action-loading="isCreating"
    @action="handleCreate"
    @closed="emit('closed')"
  >
    <div class="api-key-create-modal__form">
      <BittsInput
        v-model="keyName"
        form-label="Key name"
        name="api-key-name"
        placeholder="e.g. My integration"
        :status="isNameTaken ? 'danger' : 'default'"
        danger-text="A key with that name already exists."
      />
      <BittsAlert
        v-if="!apiKeysStore.availableScopes.length"
        color="warning"
        message="No API scopes are available for your account. Contact your administrator to create API keys."
      />
      <div v-else class="api-key-create-modal__scopes">
        <BittsInput
          v-model="scopeSearch"
          form-label="Scopes"
          name="api-key-scope-search"
          type="search"
          placeholder="Search scopes to add..."
        />
        <ApiKeyScopeBox
          title="Current Scopes"
          variant="selected"
          :scopes="selectedScopes"
          empty-text="Add at least one scope from the list below."
          @select="removeScope"
        />
        <ApiKeyScopeBox
          title="Available Scopes"
          variant="available"
          :scopes="filteredAvailableScopes"
          :empty-text="availableEmptyText"
          @select="addScope"
        />
      </div>
      <div class="api-key-create-modal__field">
        <BittsFormLabel :label="{ title: 'Allowed IPs' }" :for="allowedIpsId" />
        <BittsFilterTagInput
          :id="allowedIpsId"
          v-model="allowedIps"
          placeholder="e.g. 192.168.1.1"
        />
        <span v-if="ipsWarning" class="api-key-create-modal__hint--danger">
          {{ ipsWarning }}
        </span>
        <span v-else-if="!allowedIps.length" class="api-key-create-modal__hint">
          All IPs allowed.
        </span>
      </div>
      <div class="api-key-create-modal__field">
        <div class="api-key-create-modal__label-row">
          <BittsFormLabel
            :label="{ title: 'Expiration date' }"
            :for="expirationId"
          />
          <span class="api-key-create-modal__optional">Optional</span>
        </div>
        <div class="api-key-create-modal__expiry">
          <BittsRangePicker
            :id="expirationId"
            class="api-key-create-modal__date"
            use-single-date-picker
            :initial-date="pickerDate"
            format="MM/DD/YYYY"
            placeholder="mm/dd/yyyy"
            :disabled-date="disabledExpiryDate"
            @change="onExpiryChange"
          />
          <BittsButton
            v-if="expiresDate"
            type="neutral"
            variant="ghost"
            size="small"
            :center-icon="['fak', 'x']"
            aria-label="Clear expiration date"
            @click="expiresDate = ''"
          />
        </div>
        <span class="api-key-create-modal__hint">
          Expires at the end of the selected day, UTC. Leave blank for no
          expiration.
        </span>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsFilterTagInput,
  BittsFormLabel,
  BittsInput,
  BittsModal,
  BittsRangePicker,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { DateTime } from 'luxon';
import { computed, ref, useId } from 'vue';

import useApiKeyCreateForm from '@/composables/useApiKeyCreateForm';
import { useApiKeysStore } from '@/stores';

import ApiKeyScopeBox from '../components/ApiKeyScopeBox.vue';
import useApiControlsTracking from '../useApiControlsTracking';

import ApiKeyCreatedModal from './ApiKeyCreatedModal.vue';

const emit = defineEmits<{
  closed: [];
}>();

useHead({ title: 'Create API key - API Keys - API - Crossbeam' });

const apiKeysStore = useApiKeysStore();
const { trackApiControlAction } = useApiControlsTracking();

const {
  keyName,
  selectedScopes,
  allowedIps,
  expiresDate,
  rawKey,
  isCreating,
  ipsWarning,
  isNameTaken,
  canSubmit,
  disabledExpiryDate,
  onExpiryChange,
  createKey,
} = useApiKeyCreateForm(() => apiKeysStore.takenKeyNames);

const pickerDate = computed(() =>
  expiresDate.value
    ? DateTime.fromISO(expiresDate.value).toFormat('MM/dd/yyyy')
    : '',
);

const scopeSearch = ref('');
const allowedIpsId = useId();
const expirationId = useId();

const filteredAvailableScopes = computed(() => {
  const search = scopeSearch.value.trim().toLowerCase();
  return apiKeysStore.availableScopes.filter(
    (scope) =>
      !selectedScopes.value.includes(scope) &&
      scope.toLowerCase().includes(search),
  );
});

const availableEmptyText = computed(() =>
  scopeSearch.value.trim()
    ? 'No scopes match your search.'
    : 'All available scopes have been added.',
);

function addScope(scope: string) {
  selectedScopes.value = [...selectedScopes.value, scope];
}

function removeScope(scope: string) {
  selectedScopes.value = selectedScopes.value.filter(
    (selected) => selected !== scope,
  );
}

async function handleCreate() {
  const createdKeyId = await createKey();
  if (!createdKeyId) return;
  trackApiControlAction({
    eventSite: EVENT_SITES.API_KEY_CREATE_MODAL,
    type: 'create_submitted',
    resource: 'key',
    resourceId: createdKeyId,
  });
  apiKeysStore.refreshApiKeysStore();
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-key-create-modal.o-bitts-modal.bitts-modal--data:deep()
  .o-bitts-modal__content {
  @apply p-0 m-0;
}

.api-key-create-modal__form {
  @apply flex flex-col gap-16 p-20;
}

.api-key-create-modal__scopes {
  @apply flex flex-col gap-12;
}

.api-key-create-modal__field {
  @apply flex flex-col gap-4;
}

.api-key-create-modal__label-row {
  @apply flex items-center justify-between;
}

.api-key-create-modal__optional {
  @apply text-sm text-neutral-text-weak;
}

.api-key-create-modal__date {
  @apply flex-1;
}

.api-key-create-modal__date :deep(.ant-picker) {
  @apply w-full;
}

.api-key-create-modal__expiry {
  @apply flex items-center gap-8;
}

.api-key-create-modal__hint {
  @apply text-sm text-neutral-text;
}

.api-key-create-modal__hint--danger {
  @apply text-sm text-danger-text;
}
</style>
